import { type ReactNode, useState, useEffect } from 'react';
import { useSearch } from 'wouter';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { isValidPhoneNumber } from 'libphonenumber-js';
import { useTranslation } from 'react-i18next';
import {
  Building2,
  Clock,
  Globe,
  Save,
  Upload,
  Settings as SettingsIcon,
  Calendar,
  User,
  Mail,
  Phone,
  MapPin,
  Loader2,
  CheckCircle,
  XCircle,
  ChevronDown,
  Code2
} from 'lucide-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PhoneInput } from '@/components/ui/phone-input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { EmbedSettings } from '@/pages/admin/booking-page/EmbedSettings';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from '@/components/ui/form';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useToast } from '@/hooks/use-toast';
import { useSetPageHeader } from '@/components/chrome/PageHeader';
import { organizationService } from '@/services/api/organization.service';
import { ProfileSettingsPanel } from '@/components/settings/ProfileSettingsPanel';
import { Checkbox } from '@/components/ui/checkbox';
import { SUPPORTED_LANGUAGES } from '@/lib/i18n/languages';

// Common timezones with translation keys
const TIMEZONES = [
  { value: 'America/Los_Angeles', labelKey: 'timezones.usPacific' },
  { value: 'America/Chicago', labelKey: 'timezones.usCentral' },
  { value: 'America/New_York', labelKey: 'timezones.usEastern' },
  { value: 'America/Anchorage', labelKey: 'timezones.usAlaska' },
  { value: 'Pacific/Honolulu', labelKey: 'timezones.usHawaii' },
  { value: 'America/Sao_Paulo', labelKey: 'timezones.brazilSaoPaulo' },
  { value: 'America/Manaus', labelKey: 'timezones.brazilManaus' },
  { value: 'America/Fortaleza', labelKey: 'timezones.brazilFortaleza' },
  { value: 'Europe/London', labelKey: 'timezones.londonGmt' },
  { value: 'Europe/Paris', labelKey: 'timezones.parisCet' },
  { value: 'Europe/Berlin', labelKey: 'timezones.berlinCet' },
  { value: 'Europe/Warsaw', labelKey: 'timezones.warsawCet' },
  { value: 'Europe/Lisbon', labelKey: 'timezones.lisbonWet' },
  { value: 'Asia/Tokyo', labelKey: 'timezones.tokyoJst' },
  { value: 'Asia/Shanghai', labelKey: 'timezones.shanghaicst' },
  { value: 'Australia/Sydney', labelKey: 'timezones.sydneyAedt' },
  { value: 'UTC', labelKey: 'timezones.utc' },
];

// Supported email languages
const EMAIL_LANGUAGES = [
  { value: 'en', labelKey: 'emailLanguage.languages.en' },
  { value: 'pt-BR', labelKey: 'emailLanguage.languages.ptBR' },
  { value: 'pt-PT', labelKey: 'emailLanguage.languages.ptPT' },
  { value: 'de', labelKey: 'emailLanguage.languages.de' },
  { value: 'pl', labelKey: 'emailLanguage.languages.pl' },
];

const LOCALE_DEFAULTS: Record<string, { currency: string; locale: string }> = {
  pl:     { currency: 'PLN', locale: 'pl' },
  de:     { currency: 'EUR', locale: 'de' },
  'pt-BR': { currency: 'BRL', locale: 'pt-BR' },
  'pt-PT': { currency: 'EUR', locale: 'pt-PT' },
  en:     { currency: 'EUR', locale: 'en' },
};

function getLocaleDefaults(language: string) {
  return LOCALE_DEFAULTS[language] ?? LOCALE_DEFAULTS['en'];
}

const CURRENCIES = [
  { value: 'USD', label: 'US Dollar (USD)' },
  { value: 'EUR', label: 'Euro (EUR)' },
  { value: 'BRL', label: 'Brazilian Real (BRL)' },
  { value: 'PLN', label: 'Polish Zloty (PLN)' },
];

function SettingsSection({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: ReactNode;
}) {
  return (
    <section className="grid grid-cols-1 lg:grid-cols-3 gap-x-8 gap-y-4 py-8 first:pt-0">
      <div className="lg:col-span-1">
        <h3 className="text-sm font-semibold text-ink">{title}</h3>
        {description && (
          <p className="text-body-xs text-soft mt-1 leading-relaxed">{description}</p>
        )}
      </div>
      <div className="lg:col-span-2 space-y-4">{children}</div>
    </section>
  );
}

// Note: Schemas are created inside the component to access t() function

// Type definitions (schemas will be created inside component)
type OrganizationFormData = {
  name: string;
  description?: string;
  email?: string;
  phone?: string;
  address?: string;
  website?: string;
  logo_url?: string;
  homepage_url?: string;
  public_locales: string[];
  timezone: string;
  currency: string;
  locale: string;
};

type BookingSettingsFormData = {
  advance_booking_days: number;
  minimum_notice_hours: number;
  cancellation_hours: number;
  buffer_time_minutes: number;
  slot_duration_minutes: number;
  max_bookings_per_day: number;
  require_phone: boolean;
  require_address: boolean;
  auto_confirm_bookings: boolean;
  send_reminders: boolean;
  reminder_hours: number;
  allow_customer_cancellation: boolean;
  allow_customer_rescheduling: boolean;
};

export default function AdminSettingsPage() {
  const { t, i18n } = useTranslation(['settings', 'common', 'validation']);
  const search = useSearch();
  const [activeTab, setActiveTab] = useState(
    () => new URLSearchParams(search).get('tab') || 'profile',
  );

  // Sync the active tab when the ?tab= query changes (e.g. deep link from the
  // sidebar profile link while already on the settings page).
  useEffect(() => {
    const tab = new URLSearchParams(search).get('tab');
    if (tab) setActiveTab(tab);
  }, [search]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [originalCurrency, setOriginalCurrency] = useState<string>('');
  const [pendingCurrency, setPendingCurrency] = useState<string | null>(null);
  const [showCurrencyWarning, setShowCurrencyWarning] = useState(false);
  const [logoStatus, setLogoStatus] = useState<'idle' | 'loading' | 'loaded' | 'error'>('idle');
  const { toast } = useToast();

  useSetPageHeader({ title: t('settings:title'), sub: t('settings:subtitle') }, [t]);

  // Create schemas inside component to access t() function
  const organizationSchema = z.object({
    name: z.string().min(1, t('validation:name.required')).max(100),
    description: z.string().max(500).optional(),
    email: z.string().email(t('validation:email.invalid')).optional(),
    phone: z.string()
      .optional()
      .or(z.literal(''))
      .refine(
        (val) => !val || val === '' || isValidPhoneNumber(val),
        t('validation:phone.invalid')
      ),
    address: z.string().max(200).optional(),
    website: z.string().url(t('validation:url.invalid')).optional().or(z.literal('')),
    logo_url: z.string().url(t('validation:url.invalid')).optional().or(z.literal('')),
    homepage_url: z.string().url(t('validation:url.invalid')).optional().or(z.literal('')),
    public_locales: z.array(z.string()).min(1, t('validation:publicLocales.required', 'At least one language is required')),
    timezone: z.string().min(1, t('validation:timezone.required')),
    currency: z.enum(['USD', 'EUR', 'BRL', 'PLN'] as const),
    locale: z.enum(['en', 'pt-BR', 'pt-PT', 'de', 'pl'] as const),
  });

  const bookingSettingsSchema = z.object({
    advance_booking_days: z.number().min(1).max(365),
    minimum_notice_hours: z.number().min(0).max(168),
    cancellation_hours: z.number().min(0).max(168),
    buffer_time_minutes: z.number().min(0).max(60),
    slot_duration_minutes: z.number().min(5).max(120),
    max_bookings_per_day: z.number().min(1).max(100),
    require_phone: z.boolean(),
    require_address: z.boolean(),
    auto_confirm_bookings: z.boolean(),
    send_reminders: z.boolean(),
    reminder_hours: z.number().min(1).max(48),
    allow_customer_cancellation: z.boolean(),
    allow_customer_rescheduling: z.boolean(),
  });


  // Organization form
  const organizationForm = useForm<OrganizationFormData>({
    resolver: zodResolver(organizationSchema),
    defaultValues: {
      name: '',
      description: '',
      email: '',
      phone: '',
      address: '',
      website: '',
      logo_url: '',
      homepage_url: '',
      public_locales: ['en', 'pt-BR', 'pt-PT', 'de', 'pl'],
      timezone: 'America/New_York',
      currency: getLocaleDefaults(i18n.language).currency,
      locale: getLocaleDefaults(i18n.language).locale,
    },
  });

  // Booking settings form
  const bookingForm = useForm<BookingSettingsFormData>({
    resolver: zodResolver(bookingSettingsSchema),
    defaultValues: {
      advance_booking_days: 30,
      minimum_notice_hours: 0,
      cancellation_hours: 24,
      buffer_time_minutes: 0,
      slot_duration_minutes: 30,
      // Mirrors Organization::DEFAULT_SETTINGS on the API side — a form default
      // that disagrees with the backend's silently rewrites the value on save.
      max_bookings_per_day: 10,
      require_phone: false,
      require_address: false,
      auto_confirm_bookings: false,
      send_reminders: true,
      reminder_hours: 24,
      allow_customer_cancellation: true,
      allow_customer_rescheduling: true,
    },
  });

  // Watch cancel/reschedule settings to conditionally show cancellation_hours
  const allowCancellation = bookingForm.watch('allow_customer_cancellation');
  const allowRescheduling = bookingForm.watch('allow_customer_rescheduling');
  const showCancellationHours = allowCancellation || allowRescheduling;

  // Load organization data on mount
  useEffect(() => {
    loadOrganizationData();
  }, []);

  const loadOrganizationData = async () => {
    try {
      setIsLoading(true);
      const org = await organizationService.getOrganization();

      // Track original currency, falling back to locale-derived default for new orgs
      const localeDefaults = getLocaleDefaults(i18n.language);
      const orgCurrency = org.currency || localeDefaults.currency;
      setOriginalCurrency(orgCurrency);

      // Set organization form data
      // Normalize phone number to E.164 format if it's not already
      let normalizedPhone = org.phone || '';
      if (normalizedPhone && !normalizedPhone.startsWith('+')) {
        // If phone doesn't start with +, assume US number and prepend +1
        normalizedPhone = '+1' + normalizedPhone.replace(/\D/g, '');
      }

      organizationForm.reset({
        name: org.name || '',
        description: org.description || '',
        email: org.email || '',
        phone: normalizedPhone,
        address: org.address || '',
        website: org.website || '',
        logo_url: org.logo_url || '',
        homepage_url: org.homepage_url || '',
        public_locales: org.public_locales || ['en', 'pt-BR', 'pt-PT', 'de', 'pl'],
        timezone: org.timezone || 'America/New_York',
        currency: orgCurrency,
        locale: org.locale || localeDefaults.locale,
      });

      // Set booking settings if available
      const bookingSettings = (org as any).settings?.booking || org.booking_settings;
      if (bookingSettings) {
        bookingForm.reset({
          // `??` not `||`: 0 is a meaningful value for the hour-based settings
          // (same-day booking, cancel any time up to the appointment), and `||`
          // silently replaced it with the fallback on load and on the next save.
          advance_booking_days: bookingSettings.advance_booking_days || 30,
          minimum_notice_hours: bookingSettings.minimum_notice_hours ?? 0,
          cancellation_hours: bookingSettings.cancellation_hours ?? 24,
          buffer_time_minutes: bookingSettings.buffer_time_minutes ?? 0,
          slot_duration_minutes: bookingSettings.slot_duration_minutes || 30,
          max_bookings_per_day: bookingSettings.max_bookings_per_day || 10,
          require_phone: bookingSettings.require_phone ?? false,
          require_address: bookingSettings.require_address ?? false,
          auto_confirm_bookings: bookingSettings.auto_confirm_bookings ?? false,
          send_reminders: bookingSettings.send_reminders ?? true,
          reminder_hours: bookingSettings.reminder_hours || 24,
          // reminder_hours has a minimum of 1, so `||` is safe here.
          allow_customer_cancellation: bookingSettings.allow_customer_cancellation ?? true,
          allow_customer_rescheduling: bookingSettings.allow_customer_rescheduling ?? true,
        });
      }
    } catch (error) {
      console.error('Failed to load organization data:', error);
      toast({
        title: t('common:toasts.error'),
        description: t('settings:toasts.loadError'),
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleOrganizationSubmit = async (data: OrganizationFormData) => {
    try {
      setIsSaving(true);
      await organizationService.updateOrganization(data);
      toast({
        title: t('common:toasts.success'),
        description: t('settings:toasts.profileSuccess'),
      });
    } catch (error: any) {
      toast({
        title: t('common:toasts.error'),
        description: error.message || t('settings:toasts.profileError'),
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleBookingSettingsSubmit = async (data: BookingSettingsFormData) => {
    try {
      setIsSaving(true);
      await organizationService.updateSettings({
        booking_settings: data,
      });

      // Reload organization data to reflect the changes
      await loadOrganizationData();

      toast({
        title: t('common:toasts.success'),
        description: t('settings:toasts.bookingSuccess'),
      });
    } catch (error: any) {
      toast({
        title: t('common:toasts.error'),
        description: error.message || t('settings:toasts.bookingError'),
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-soft" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Settings Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <Card>
          <CardHeader className="px-4 sm:px-6 pt-4 pb-4">
            <TabsList>
              <TabsTrigger value="profile" aria-label={t('settings:tabs.profile')}>
                <Building2 className="h-4 w-4 flex-shrink-0" />
                <span className="truncate">{t('settings:tabs.profile')}</span>
              </TabsTrigger>
              <TabsTrigger value="booking" aria-label={t('settings:tabs.booking')}>
                <Calendar className="h-4 w-4 flex-shrink-0" />
                <span className="truncate">{t('settings:tabs.booking')}</span>
              </TabsTrigger>
              <TabsTrigger value="embed" aria-label={t('settings:tabs.embed', 'Embed')}>
                <Code2 className="h-4 w-4 flex-shrink-0" />
                <span className="truncate">{t('settings:tabs.embed', 'Embed')}</span>
              </TabsTrigger>
              <TabsTrigger value="myProfile" aria-label={t('settings:tabs.myProfile', 'Profile')}>
                <User className="h-4 w-4 flex-shrink-0" />
                <span className="truncate">{t('settings:tabs.myProfile', 'Profile')}</span>
              </TabsTrigger>
            </TabsList>
          </CardHeader>

          {/* Profile Tab */}
          <TabsContent value="profile" className="mt-0">
            <CardContent className="px-4 sm:px-6 pt-6">
              <Form {...organizationForm}>
                <form onSubmit={organizationForm.handleSubmit(handleOrganizationSubmit)} className="divide-y divide-line">
                  <SettingsSection
                    title={t('settings:profile.sections.identity', 'Brand Identity')}
                    description={t('settings:profile.sections.identityHelp', 'Your business name, description, logo and links shown to customers.')}
                  >
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <FormField
                        control={organizationForm.control}
                        name="name"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel required>{t('settings:profile.form.name')}</FormLabel>
                            <FormControl>
                              <Input
                                placeholder={t('settings:profile.form.namePlaceholder')}
                                autoComplete="organization"
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={organizationForm.control}
                        name="homepage_url"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t('settings:profile.form.homepageUrl')}</FormLabel>
                            <FormControl>
                              <div className="relative">
                                <Globe className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-soft" />
                                <Input
                                  type="url"
                                  placeholder={t('settings:profile.form.homepageUrlPlaceholder')}
                                  className="pl-10"
                                  autoComplete="url"
                                  inputMode="url"
                                  {...field}
                                />
                              </div>
                            </FormControl>
                            <FormDescription>
                              {t('settings:profile.form.homepageUrlHelp')}
                            </FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <FormField
                        control={organizationForm.control}
                        name="description"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t('settings:profile.form.description')}</FormLabel>
                            <FormControl>
                              <Textarea
                                placeholder={t('settings:profile.form.descriptionPlaceholder')}
                                rows={4}
                                className="min-h-[96px]"
                                {...field}
                              />
                            </FormControl>
                            <FormDescription>
                              {t('settings:profile.form.descriptionHelp')}
                            </FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={organizationForm.control}
                        name="logo_url"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t('settings:profile.form.logoUrl')}</FormLabel>
                            <FormControl>
                              <div className="space-y-4">
                                <Input
                                  type="url"
                                  placeholder={t('settings:profile.form.logoPlaceholder')}
                                  autoComplete="url"
                                  inputMode="url"
                                  {...field}
                                  onChange={(e) => {
                                    field.onChange(e);
                                    // Reset to idle if empty, otherwise set to loading
                                    setLogoStatus(e.target.value ? 'loading' : 'idle');
                                  }}
                                />

                                {/* Format instructions — collapsed by default to keep the form short on mobile */}
                                <Collapsible>
                                  <CollapsibleTrigger className="flex items-center gap-1.5 text-xs font-medium text-brand-700 hover:text-brand-900 [&[data-state=open]>svg]:rotate-180">
                                    <ChevronDown className="h-3.5 w-3.5 transition-transform" aria-hidden="true" />
                                    {t('settings:profile.form.logoRequirements', 'Logo Requirements')}
                                  </CollapsibleTrigger>
                                  <CollapsibleContent className="mt-2">
                                    <ul className="text-body-xs text-soft space-y-0.5 list-disc list-inside pl-1">
                                      <li>{t('settings:profile.form.logoFormat', 'Format: PNG, JPG, or SVG (PNG with transparent background recommended)')}</li>
                                      <li>{t('settings:profile.form.logoSize', 'Recommended size: 200x200 pixels or larger')}</li>
                                      <li>{t('settings:profile.form.logoRatio', 'Aspect ratio: Square or horizontal logos work best')}</li>
                                    </ul>
                                  </CollapsibleContent>
                                </Collapsible>

                                {field.value && (
                                  <div className="space-y-4">
                                    {/* Image validation */}
                                    <div className="p-4 border border-line rounded-lg bg-surface-2">
                                      <div className="flex items-center justify-between mb-2">
                                        <p className="text-mono-eyebrow uppercase text-soft">
                                          {t('settings:profile.form.logoPreview')}
                                        </p>
                                        {logoStatus === 'loaded' && (
                                          <Badge tone="success">
                                            <CheckCircle className="h-3 w-3 mr-1" />
                                            {t('settings:profile.form.logoValid', 'Valid')}
                                          </Badge>
                                        )}
                                        {logoStatus === 'error' && (
                                          <Badge tone="danger">
                                            <XCircle className="h-3 w-3 mr-1" />
                                            {t('settings:profile.form.logoError', 'Failed to load')}
                                          </Badge>
                                        )}
                                      </div>
                                      <img
                                        src={field.value}
                                        alt={t('settings:profile.form.logoAlt')}
                                        className={`max-h-32 object-contain ${logoStatus === 'error' ? 'hidden' : ''}`}
                                        onLoad={() => setLogoStatus('loaded')}
                                        onError={() => setLogoStatus('error')}
                                      />
                                      {logoStatus === 'error' && (
                                        <p className="text-sm text-danger">
                                          {t('settings:profile.form.logoErrorMessage', 'Could not load image. Please check the URL.')}
                                        </p>
                                      )}
                                    </div>

                                    {/* Booking page preview */}
                                    {logoStatus === 'loaded' && (
                                      <div className="border rounded-lg overflow-hidden">
                                        <div className="bg-surface-2 px-3 py-1.5 border-b">
                                          <p className="text-xs font-medium text-soft">{t('settings:profile.form.bookingPagePreview', 'Preview: How it looks on booking page')}</p>
                                        </div>
                                        <div className="bg-white p-4">
                                          <div className="flex items-center space-x-4">
                                            <img
                                              src={field.value}
                                              alt="Logo"
                                              className="h-12 max-w-[180px] object-contain"
                                            />
                                          </div>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            </FormControl>
                            <FormDescription>{t('settings:profile.form.logoHelp')}</FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                  </SettingsSection>

                  <SettingsSection
                    title={t('settings:profile.sections.contact', 'Contact')}
                    description={t('settings:profile.sections.contactHelp', 'How customers can reach you.')}
                  >
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <FormField
                        control={organizationForm.control}
                        name="email"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t('settings:profile.form.email')}</FormLabel>
                            <FormControl>
                              <div className="relative">
                                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-soft" />
                                <Input
                                  type="email"
                                  className="pl-10"
                                  placeholder={t('settings:profile.form.emailPlaceholder')}
                                  autoComplete="email"
                                  {...field}
                                />
                              </div>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={organizationForm.control}
                        name="phone"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t('settings:profile.form.phone')}</FormLabel>
                            <FormControl>
                              <PhoneInput
                                value={field.value}
                                onChange={field.onChange}
                                placeholder={t('settings:profile.form.phonePlaceholder')}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <FormField
                      control={organizationForm.control}
                      name="address"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t('settings:profile.form.address')}</FormLabel>
                          <FormControl>
                            <div className="relative">
                              <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-soft" />
                              <Input
                                className="pl-10"
                                placeholder={t('settings:profile.form.addressPlaceholder')}
                                autoComplete="street-address"
                                {...field}
                              />
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={organizationForm.control}
                      name="website"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t('settings:profile.form.website')}</FormLabel>
                          <FormControl>
                            <div className="relative">
                              <Globe className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-soft" />
                              <Input
                                type="url"
                                className="pl-10"
                                placeholder={t('settings:profile.form.websitePlaceholder')}
                                autoComplete="url"
                                inputMode="url"
                                {...field}
                              />
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </SettingsSection>

                  <SettingsSection
                    title={t('settings:profile.sections.localization', 'Localization')}
                    description={t('settings:profile.sections.localizationHelp', 'Timezone, currency and languages for your booking page.')}
                  >
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <FormField
                        control={organizationForm.control}
                        name="timezone"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel required>{t('settings:profile.form.timezone')}</FormLabel>
                            <Select onValueChange={field.onChange} value={field.value}>
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder={t('settings:profile.form.timezonePlaceholder')} />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {TIMEZONES.map((tz) => (
                                  <SelectItem key={tz.value} value={tz.value}>
                                    {t(`settings:${tz.labelKey}`)}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={organizationForm.control}
                        name="currency"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel required>{t('settings:profile.form.currency')}</FormLabel>
                            <Select
                              onValueChange={(value) => {
                                // Check if currency is being changed
                                if (originalCurrency && value !== originalCurrency) {
                                  setPendingCurrency(value);
                                  setShowCurrencyWarning(true);
                                } else {
                                  field.onChange(value);
                                }
                              }}
                              value={field.value}
                            >
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder={t('settings:profile.form.currencyPlaceholder')} />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {CURRENCIES.map((curr) => (
                                  <SelectItem key={curr.value} value={curr.value}>
                                    {curr.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={organizationForm.control}
                        name="locale"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel required>{t('settings:emailLanguage.label')}</FormLabel>
                            <Select onValueChange={field.onChange} value={field.value}>
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder={t('settings:emailLanguage.label')} />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {EMAIL_LANGUAGES.map((lang) => (
                                  <SelectItem key={lang.value} value={lang.value}>
                                    {t(`settings:${lang.labelKey}`)}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <FormDescription>
                              {t('settings:emailLanguage.description')}
                            </FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <FormField
                      control={organizationForm.control}
                      name="public_locales"
                      render={({ field }) => {
                        const currentValue = field.value || [];
                        const isLastSelected = currentValue.length === 1;

                        return (
                          <FormItem>
                            <FormLabel required>{t('settings:profile.form.publicLocales')}</FormLabel>
                            <FormDescription className="!mt-1">
                              {t('settings:profile.form.publicLocalesHelp')}
                            </FormDescription>
                            <div className="flex flex-wrap gap-2 pt-1">
                              {SUPPORTED_LANGUAGES.map((locale) => {
                                const isChecked = currentValue.includes(locale.code);
                                const isDisabled = isChecked && isLastSelected;

                                return (
                                  <button
                                    key={locale.code}
                                    type="button"
                                    role="switch"
                                    aria-checked={isChecked}
                                    aria-label={locale.name}
                                    disabled={isDisabled}
                                    onClick={() => {
                                      const newValue = isChecked
                                        ? currentValue.filter((v) => v !== locale.code)
                                        : [...currentValue, locale.code];
                                      field.onChange(newValue);
                                    }}
                                    className={`inline-flex items-center gap-2 rounded-full border px-3 min-h-[36px] text-sm font-medium transition-colors ${isChecked
                                        ? 'bg-brand-100 border-brand-400 text-brand-900'
                                        : 'bg-surface border-line text-ink hover:border-brand-300'
                                      } ${isDisabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
                                  >
                                    <span aria-hidden="true">{locale.flag}</span>
                                    <span>{locale.name}</span>
                                    {isChecked && (
                                      <CheckCircle className="h-3.5 w-3.5 text-brand-600" aria-hidden="true" />
                                    )}
                                  </button>
                                );
                              })}
                            </div>
                            <FormMessage />
                          </FormItem>
                        );
                      }}
                    />
                  </SettingsSection>

                  {logoStatus === 'error' && (
                    <p className="text-sm text-danger text-right pt-4">
                      {t('settings:profile.form.logoSaveBlocked', 'Please fix the logo URL before saving, or remove it.')}
                    </p>
                  )}
                  <div className="sticky bottom-0 -mx-6 px-6 py-3 bg-background/95 backdrop-blur border-t flex justify-end z-10">
                    <Button
                      type="submit"
                      disabled={isSaving || logoStatus === 'error'}
                      className="min-h-[44px] w-full sm:w-auto"
                    >
                      {isSaving ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          {t('settings:buttons.saving')}
                        </>
                      ) : (
                        <>
                          <Save className="mr-2 h-4 w-4" />
                          {t('settings:buttons.saveProfile')}
                        </>
                      )}
                    </Button>
                  </div>
                </form>
              </Form>
            </CardContent>
          </TabsContent>

          {/* Booking Settings Tab */}
          <TabsContent value="booking" className="mt-0">
            <CardContent className="px-4 sm:px-6 pt-6">
              <Form {...bookingForm}>
                <form onSubmit={bookingForm.handleSubmit(handleBookingSettingsSubmit)} className="divide-y divide-line">
                  <SettingsSection
                    title={t('settings:booking.sections.window', 'Booking Window')}
                    description={t('settings:booking.sections.windowHelp', 'How far in advance customers can book and limits per day.')}
                  >
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <FormField
                        control={bookingForm.control}
                        name="advance_booking_days"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t('settings:booking.form.advanceBookingDays')}</FormLabel>
                            <FormControl>
                              <Input
                                type="number"
                                min="1"
                                max="365"
                                {...field}
                                value={field.value ?? ''}
                                onChange={(e) => {
                                  const v = e.target.value;
                                  const n = parseInt(v, 10);
                                  field.onChange(v === '' || Number.isNaN(n) ? null : n);
                                }}
                                onBlur={(e) => {
                                  field.onBlur();
                                  const n = parseInt(e.target.value, 10);
                                  field.onChange(Number.isNaN(n) || n < 1 ? 1 : n > 365 ? 365 : n);
                                }}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={bookingForm.control}
                        name="minimum_notice_hours"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t('settings:booking.form.minimumNoticeHours')}</FormLabel>
                            <FormControl>
                              <Input
                                type="number"
                                min="0"
                                max="168"
                                {...field}
                                value={field.value ?? ''}
                                onChange={(e) => {
                                  const v = e.target.value;
                                  const n = parseInt(v, 10);
                                  field.onChange(v === '' || Number.isNaN(n) ? null : n);
                                }}
                                onBlur={(e) => {
                                  field.onBlur();
                                  const n = parseInt(e.target.value, 10);
                                  field.onChange(Number.isNaN(n) || n < 0 ? 0 : n > 168 ? 168 : n);
                                }}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={bookingForm.control}
                        name="buffer_time_minutes"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t('settings:booking.form.bufferTime')}</FormLabel>
                            <Select
                              value={(field.value ?? 0).toString()}
                              onValueChange={(value) => field.onChange(parseInt(value, 10))}
                            >
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {[0, 5, 10, 15, 30, 60].map((mins) => (
                                  <SelectItem key={mins} value={mins.toString()}>
                                    {mins === 0
                                      ? t('settings:booking.form.bufferNone', 'No buffer')
                                      : t('settings:booking.form.bufferMinutes', '{{count}} minutes', { count: mins })}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={bookingForm.control}
                        name="slot_duration_minutes"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t('settings:booking.form.slotDuration', 'Time Slot Interval')}</FormLabel>
                            <Select
                              value={(field.value ?? 30).toString()}
                              onValueChange={(value) => field.onChange(parseInt(value, 10))}
                            >
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {[5, 10, 15, 20, 30, 45, 60, 90, 120].map((mins) => (
                                  <SelectItem key={mins} value={mins.toString()}>
                                    {t('settings:booking.form.slotDurationMinutes', '{{count}} minutes', { count: mins })}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <p className="text-xs text-soft">
                              {t('settings:booking.form.slotDurationHelp', 'How often new booking slots are offered (e.g. every 15 or 30 minutes).')}
                            </p>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={bookingForm.control}
                        name="max_bookings_per_day"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t('settings:booking.form.maxBookingsPerDay')}</FormLabel>
                            <FormControl>
                              <Input
                                type="number"
                                min="1"
                                max="100"
                                {...field}
                                value={field.value ?? ''}
                                onChange={(e) => {
                                  const v = e.target.value;
                                  const n = parseInt(v, 10);
                                  field.onChange(v === '' || Number.isNaN(n) ? null : n);
                                }}
                                onBlur={(e) => {
                                  field.onBlur();
                                  const n = parseInt(e.target.value, 10);
                                  field.onChange(Number.isNaN(n) || n < 1 ? 1 : n > 100 ? 100 : n);
                                }}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </SettingsSection>

                  <SettingsSection
                    title={t('settings:booking.sections.confirmation', 'Confirmation')}
                    description={t('settings:booking.sections.confirmationHelp', 'Decide whether bookings need your approval.')}
                  >
                    <FormField
                      control={bookingForm.control}
                      name="auto_confirm_bookings"
                      render={({ field }) => (
                        <FormItem className="flex items-start gap-3 py-1 space-y-0">
                          <FormControl>
                            <Switch checked={field.value} onCheckedChange={field.onChange} className="mt-0.5" />
                          </FormControl>
                          <div className="space-y-0.5">
                            <FormLabel>{t('settings:booking.form.autoConfirmBookings')}</FormLabel>
                            <FormDescription>{t('settings:booking.form.autoConfirmBookingsHelp')}</FormDescription>
                          </div>
                        </FormItem>
                      )}
                    />

                  </SettingsSection>

                  <SettingsSection
                    title={t('settings:booking.sections.cancellation', 'Cancellation & Rescheduling')}
                    description={t('settings:booking.sections.cancellationHelp', 'What customers can change themselves and the notice required.')}
                  >
                    <div className="space-y-3">
                      <FormField
                        control={bookingForm.control}
                        name="allow_customer_cancellation"
                        render={({ field }) => (
                          <FormItem className="flex items-center gap-3 py-1 space-y-0">
                            <FormControl>
                              <Switch checked={field.value} onCheckedChange={field.onChange} />
                            </FormControl>
                            <FormLabel className="!mt-0">{t('settings:booking.form.allowCustomerCancellation')}</FormLabel>
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={bookingForm.control}
                        name="allow_customer_rescheduling"
                        render={({ field }) => (
                          <FormItem className="flex items-center gap-3 py-1 space-y-0">
                            <FormControl>
                              <Switch checked={field.value} onCheckedChange={field.onChange} />
                            </FormControl>
                            <FormLabel className="!mt-0">{t('settings:booking.form.allowCustomerRescheduling')}</FormLabel>
                          </FormItem>
                        )}
                      />
                      {showCancellationHours && (
                        <FormField
                          control={bookingForm.control}
                          name="cancellation_hours"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>{t('settings:booking.form.cancellationHours')}</FormLabel>
                              <FormControl>
                                <Input
                                  type="number"
                                  min="0"
                                  max="168"
                                  {...field}
                                  value={field.value ?? ''}
                                  onChange={(e) => {
                                    const v = e.target.value;
                                    const n = parseInt(v, 10);
                                    field.onChange(v === '' || Number.isNaN(n) ? null : n);
                                  }}
                                  onBlur={(e) => {
                                    field.onBlur();
                                    const n = parseInt(e.target.value, 10);
                                    field.onChange(Number.isNaN(n) || n < 0 ? 0 : n > 168 ? 168 : n);
                                  }}
                                />
                              </FormControl>
                              <FormDescription>{t('settings:booking.form.cancellationHoursHelp')}</FormDescription>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      )}
                    </div>

                  </SettingsSection>

                  <SettingsSection
                    title={t('settings:booking.sections.reminders', 'Reminders')}
                    description={t('settings:booking.sections.remindersHelp', 'Automatic reminders before the appointment.')}
                  >
                    <div className="space-y-3">
                      <FormField
                        control={bookingForm.control}
                        name="send_reminders"
                        render={({ field }) => (
                          <FormItem className="flex items-center gap-3 py-1 space-y-0">
                            <FormControl>
                              <Switch checked={field.value} onCheckedChange={field.onChange} />
                            </FormControl>
                            <FormLabel className="!mt-0">{t('settings:booking.form.sendReminders')}</FormLabel>
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={bookingForm.control}
                        name="reminder_hours"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t('settings:booking.form.reminderHours')}</FormLabel>
                            <FormControl>
                              <Input
                                type="number"
                                min="1"
                                max="48"
                                {...field}
                                value={field.value ?? ''}
                                onChange={(e) => {
                                  const v = e.target.value;
                                  const n = parseInt(v, 10);
                                  field.onChange(v === '' || Number.isNaN(n) ? null : n);
                                }}
                                onBlur={(e) => {
                                  field.onBlur();
                                  const n = parseInt(e.target.value, 10);
                                  field.onChange(Number.isNaN(n) || n < 1 ? 1 : n > 48 ? 48 : n);
                                }}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                  </SettingsSection>

                  <SettingsSection
                    title={t('settings:booking.sections.customerInfo', 'Required Customer Info')}
                    description={t('settings:booking.sections.customerInfoHelp', 'Fields customers must fill on the booking form.')}
                  >
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <FormField
                        control={bookingForm.control}
                        name="require_phone"
                        render={({ field }) => (
                          <FormItem className="flex items-center gap-3 py-1 space-y-0">
                            <FormControl>
                              <Switch checked={field.value} onCheckedChange={field.onChange} />
                            </FormControl>
                            <FormLabel className="!mt-0">{t('settings:booking.form.requirePhone')}</FormLabel>
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={bookingForm.control}
                        name="require_address"
                        render={({ field }) => (
                          <FormItem className="flex items-center gap-3 py-1 space-y-0">
                            <FormControl>
                              <Switch checked={field.value} onCheckedChange={field.onChange} />
                            </FormControl>
                            <FormLabel className="!mt-0">{t('settings:booking.form.requireAddress')}</FormLabel>
                          </FormItem>
                        )}
                      />
                    </div>
                  </SettingsSection>

                  <div className="sticky bottom-0 -mx-6 px-6 py-3 bg-background/95 backdrop-blur border-t flex justify-end z-10">
                    <Button type="submit" disabled={isSaving} className="min-h-[44px] w-full sm:w-auto">
                      {isSaving ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          {t('settings:buttons.saving')}
                        </>
                      ) : (
                        <>
                          <Save className="mr-2 h-4 w-4" />
                          {t('settings:buttons.saveBookingSettings')}
                        </>
                      )}
                    </Button>
                  </div>
                </form>
              </Form>
            </CardContent>
          </TabsContent>

          {/* Embed Tab */}
          <TabsContent value="embed" className="mt-0">
            <CardContent className="px-4 sm:px-6 pt-6">
              <EmbedSettings />
            </CardContent>
          </TabsContent>

          {/* Personal Profile Tab */}
          <TabsContent value="myProfile" className="mt-0">
            <CardContent className="px-4 sm:px-6 pt-6">
              <ProfileSettingsPanel />
            </CardContent>
          </TabsContent>
        </Card>
      </Tabs>

      {/* Currency Change Warning Dialog */}
      <AlertDialog open={showCurrencyWarning} onOpenChange={setShowCurrencyWarning}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-danger-text">
              {t('settings:currencyWarning.title')}
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              <p className="font-semibold text-warn-text">
                {t('settings:currencyWarning.warning')}
              </p>
              <p>{t('settings:currencyWarning.description')}</p>
              <div className="bg-warn-soft border border-warn/30 rounded p-3 my-2">
                <p className="text-sm font-medium text-warn-text">
                  {t('settings:currencyWarning.example')}
                </p>
              </div>
              <p className="font-semibold">{t('settings:currencyWarning.confirm')}</p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setPendingCurrency(null)}>
              {t('common:actions.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-danger text-white hover:bg-danger-text"
              onClick={() => {
                if (pendingCurrency) {
                  organizationForm.setValue('currency', pendingCurrency);
                  setOriginalCurrency(pendingCurrency);
                  setPendingCurrency(null);
                }
              }}
            >
              {t('settings:currencyWarning.proceed')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}